/**
 * Общие части каталога программ обучения (training-packages-approval; ТЗ 26.10.2026, п.4.4.2–4.4.3):
 * плашка статуса подтверждения, строка «кто и когда добавил / почему отклонено», диалог
 * отклонения с причиной и выбор программы каталога поиском (вкладки «Пакеты» и «Сопоставление»).
 */
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react'
import type { CatalogStatus, Program } from '@/lib/api'
import { formatCatalogDate, programLine } from '@/lib/training-catalog'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'
import { Input } from '@/design-system/components/primitives/input'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'

/** Плашка статуса: «На подтверждении» / «Отклонено» / «В архиве»; у действующего — нет. */
export function CatalogStatusBadge({ status, archived }: { status: CatalogStatus; archived: boolean }) {
  if (status === 'pending') {
    return (
      <Badge status="warning" className="ml-2 align-middle">
        На подтверждении
      </Badge>
    )
  }
  if (status === 'rejected') {
    return (
      <Badge status="danger" className="ml-2 align-middle">
        Отклонено
      </Badge>
    )
  }
  if (archived) {
    return (
      <Badge variant="gray" className="ml-2 align-middle">
        В архиве
      </Badge>
    )
  }
  return null
}

/** «Добавил: Иванов И.И. · 28.10.2026» и причина отклонения — у неподтверждённого. */
export function ProposalMeta({
  status,
  createdByName,
  createdAt,
  reviewNote,
}: {
  status: CatalogStatus
  createdByName?: string
  createdAt?: string
  reviewNote?: string
}) {
  if (status === 'active') return null
  const who = [createdByName ? `Автор: ${createdByName}` : '', formatCatalogDate(createdAt)].filter(Boolean).join(' · ')
  return (
    <>
      {who ? <div className="text-12 text-txt-tertiary">{who}</div> : null}
      {status === 'rejected' && reviewNote ? (
        <div className="break-words text-12 text-red">Причина отклонения: {reviewNote}</div>
      ) : null}
    </>
  )
}

/** Диалог «Отклонить» с обязательной причиной — её увидит автор. */
export function RejectDialog({
  open,
  title,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  busy: boolean
  onConfirm: (reason: string) => void
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const id = useId()
  const trimmed = reason.trim()
  // Окно открыли заново — пустая причина (после успешного отклонения родитель закрывает окно сам,
  // и прежняя причина иначе подставилась бы в следующее отклонение).
  useEffect(() => {
    if (open) setReason('')
  }, [open])
  const close = () => {
    setReason('')
    onClose()
  }
  return (
    <ConfirmDialog
      open={open}
      title={title}
      message="Причину отклонения увидит автор в уведомлении и в каталоге."
      details={
        <div>
          <label htmlFor={id} className="mb-1.5 block text-13 font-medium text-txt">
            Причина
          </label>
          <textarea
            id={id}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none placeholder:text-txt-tertiary focus-visible:ring-2 focus-visible:ring-accent"
            placeholder="Например: такая программа уже есть в каталоге"
          />
        </div>
      }
      buttons={[
        {
          label: busy ? 'Отклонение…' : 'Отклонить',
          variant: 'danger',
          disabled: busy || trimmed === '',
          onClick: () => onConfirm(trimmed),
        },
        { label: 'Отмена', variant: 'secondary', disabled: busy, onClick: close },
      ]}
      onClose={close}
      closeDisabled={busy}
    />
  )
}

/**
 * Выбор программы каталога поиском по коду и наименованиям (первые 50 совпадений): замена при
 * сопоставлении, программа в составе пакета. Дерево каталога (ProgramTreePicker) — в выборе
 * программ клиента.
 */
export function ProgramPickerDialog({
  title,
  subtitle,
  programs,
  sectionOf,
  onPick,
  onClose,
}: {
  title: string
  subtitle?: string
  programs: Program[]
  sectionOf: (p: Program) => string
  onPick: (p: Program) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const found = useMemo(() => {
    const q = query.trim().toLowerCase().replaceAll('ё', 'е')
    if (!q) return programs.slice(0, 50)
    return programs
      .filter((p) => [p.code, p.name, p.shortName].some((v) => v.toLowerCase().replaceAll('ё', 'е').includes(q)))
      .slice(0, 50)
  }, [programs, query])
  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent title={title} className="max-w-2xl">
        <Card header={title}>
          {subtitle ? <p className="mb-3 text-14 text-txt-secondary">{subtitle}</p> : null}
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Код или наименование"
            aria-label="Поиск программы каталога"
          />
          <ul className="mt-3 max-h-96 divide-y divide-line overflow-y-auto" aria-label="Программы каталога">
            {found.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="ds-focus-ring rounded-6 w-full p-2 text-left hover:bg-hover"
                  onClick={() => onPick(p)}
                >
                  <span className="block text-14 font-medium text-txt">
                    <span className="mr-2 font-normal text-txt-secondary">{p.code}</span>
                    {programLine(p)}
                  </span>
                  <span className="block text-12 text-txt-tertiary">{sectionOf(p)}</span>
                </button>
              </li>
            ))}
            {found.length === 0 ? (
              <li className="px-2 py-6 text-center text-14 text-txt-secondary">Ничего не найдено</li>
            ) : null}
          </ul>
          <div className="mt-3 flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              Отмена
            </Button>
          </div>
        </Card>
      </DialogContent>
    </Dialog>
  )
}

/** Подтверждение действия в каталоге (в архив, удалить): заголовок, последствия и кнопка. */
export interface CatalogConfirm {
  title: string
  message: string
  confirmLabel: string
  danger?: boolean
  onConfirm: () => void
}

/**
 * Подтверждение действий каталога (training-review-3): «В архив» и «Удалить» не срабатывают с
 * одного щелчка. Возвращает окно (отрисовать в компоненте) и функцию его открытия.
 */
export function useCatalogConfirm(): [ReactNode, (c: CatalogConfirm) => void] {
  const [pending, setPending] = useState<CatalogConfirm | null>(null)
  const dialog = (
    <ConfirmDialog
      open={pending !== null}
      title={pending?.title ?? ''}
      message={pending?.message ?? ''}
      buttons={[
        {
          label: pending?.confirmLabel ?? 'Подтвердить',
          variant: pending?.danger ? 'danger' : 'primary',
          onClick: () => {
            const run = pending?.onConfirm
            setPending(null)
            run?.()
          },
        },
        { label: 'Отмена', variant: 'secondary', onClick: () => setPending(null) },
      ]}
      onClose={() => setPending(null)}
    />
  )
  return [dialog, setPending]
}
